import { type ChangeEvent, useState } from 'react'
import type { Table } from '@tanstack/react-table'
import {
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
} from 'lucide-react'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from 'shared/components/ui/select'
import { useTranslate } from 'shared/lib/i18n'
import { updateStorage } from 'shared/lib/storage'
import { cn } from 'shared/lib/utils'
import { debounce } from 'shared/lib/utils/debounce'

import { Input } from '../ui/input'

interface DataTablePaginationProps<TData> extends React.ComponentProps<'div'> {
	table: Table<TData>
	pageSizeOptions?: number[]
}

const deb = debounce((v: number, setPage: (v: number) => void) => {
	setPage(v - 1)
}, 800)

export function DataTablePagination<TData>({
	table,
	className,
	...props
}: DataTablePaginationProps<TData>) {
	const { t } = useTranslate()
	const tableState = table.getState()
	const pageCount = table.getPageCount()
	const tablePage = tableState.pagination.pageIndex + 1
	const [pageIndex, setPageIndex] = useState<number>(tablePage)
	// The field follows the table when its page changes elsewhere (the
	// arrows, a new page size) — adjusted while rendering, not in an effect.
	const [shownTablePage, setShownTablePage] = useState(tablePage)
	if (shownTablePage !== tablePage) {
		setShownTablePage(tablePage)
		setPageIndex(tablePage)
	}

	async function handleChange(e: ChangeEvent<HTMLInputElement>) {
		const value =
			Number(e.target.value) > pageCount ? pageCount : Number(e.target.value)
		setPageIndex(value)

		deb(+value, table.setPageIndex)
	}

	return (
		<div>
			<div className={cn('flex flex-wrap', className)} {...props}>
				<div className='flex items-center mr-[15px]'>
					<span className='flex items-center gap-1 '>
						<p className='mr-[10px] text-sm'>{t('dataTable.page')}</p>
						<Input
							className='w-20'
							type='number'
							value={pageIndex}
							onChange={handleChange}
						/>
						<p className='ml-[10px] text-sm'>{t('dataTable.pageOf', { count: String(table.getPageCount()) })}</p>
					</span>
				</div>
				<div className='flex items-center grow'>
					<button
						className='ls-table__footer-btn'
						onClick={() => table.setPageIndex(0)}
						disabled={!table.getCanPreviousPage()}
					>
						<ChevronsLeft />
					</button>
					<button
						className='ls-table__footer-btn'
						onClick={() => table.previousPage()}
						disabled={!table.getCanPreviousPage()}
					>
						<ChevronLeft />
					</button>
					<button
						className='ls-table__footer-btn'
						onClick={() => table.nextPage()}
						disabled={!table.getCanNextPage()}
					>
						<ChevronRight />
					</button>
					<button
						className='ls-table__footer-btn'
						onClick={() => table.setPageIndex(table.getPageCount() - 1)}
						disabled={!table.getCanNextPage()}
					>
						<ChevronsRight />
					</button>

					<div className='flex gap-3 items-center ml-auto'>
						<div className='text-sm text-muted-foreground'>
							{t('dataTable.total', { count: String(Math.max(table.getRowCount(), 0)) })}
						</div>

						<Select
							value={`${tableState.pagination.pageSize}`}
							onValueChange={(value) => {
								table.setPageSize(Number(value))
								updateStorage('user-preferences', {
									tableState: { pageSize: Number(value) },
								})
							}}
						>
							<SelectTrigger className='h-8 w-[70px]'>
								<SelectValue
									placeholder={`${tableState.pagination.pageSize}`}
								/>
							</SelectTrigger>
							<SelectContent side='top'>
								{[10, 20, 30, 40, 50, 100].map((pageSize) => (
									<SelectItem key={pageSize} value={`${pageSize}`}>
										{pageSize}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
			</div>

			{Object.keys(tableState.rowSelection).length > 0 && (
				<div className='flex-1 text-sm text-muted-foreground'>
					{table.getFilteredSelectedRowModel().rows.length} of{' '}
					{table.getFilteredRowModel().rows.length} row(s) selected.
				</div>
			)}
		</div>
	)
}
